import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Loader2, Sparkles, User, RefreshCw, QrCode } from "lucide-react";
import QRScanner from "../components/QRScanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate or retrieve device ID
const getDeviceId = () => {
  let deviceId = localStorage.getItem("carousel_device_id");
  if (!deviceId) {
    deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("carousel_device_id", deviceId);
  }
  return deviceId;
};

const JoinSession = () => {
  const navigate = useNavigate();
  const { code } = useParams();
  const [joinCode, setJoinCode] = useState(code || "");
  const [participantName, setParticipantName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(code ? "name" : "code"); // "code", "name", or "recover"
  const [isRecovering, setIsRecovering] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  // Load saved name
  useEffect(() => {
    const savedName = localStorage.getItem("carousel_participant_name");
    if (savedName) {
      setParticipantName(savedName);
    }
  }, []);

  useEffect(() => {
    // If there's a code in URL, go straight to name step
    if (code) {
      setJoinCode(code.toUpperCase());
      // Check if we're already in this session
      const storedSession = localStorage.getItem("carousel_session");
      if (storedSession) {
        const { joinCode: storedCode, sessionId } = JSON.parse(storedSession);
        if (storedCode === code.toUpperCase()) {
          // Same session, just navigate
          navigate(`/session/${sessionId}`);
          return;
        }
        // Different session - clear old one
        localStorage.removeItem("carousel_session");
      }
      setStep("name");
    }
  }, [code, navigate]);

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (joinCode.trim().length >= 4) {
      setStep("name");
    } else {
      toast.error("Please enter a valid code");
    }
  };

  const handleQRScan = (scannedCode) => {
    setShowScanner(false);
    setJoinCode(scannedCode);
    toast.success(`Code scanned: ${scannedCode}`);
    setStep("name");
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    
    if (!participantName.trim()) {
      toast.error("Please enter your name");
      return;
    }

    const trimmedCode = joinCode.trim().toUpperCase();
    setIsLoading(true);
    
    try {
      // Save name for future
      localStorage.setItem("carousel_participant_name", participantName.trim());
      
      // Clear any existing session before joining new one
      localStorage.removeItem("carousel_session");
      
      const deviceId = getDeviceId();
      const response = await axios.post(`${API}/sessions/join`, {
        device_id: deviceId,
        join_code: trimmedCode,
        name: participantName.trim()
      });

      const { session_id, group_id, group_number } = response.data;
      
      // Store session info for persistence
      localStorage.setItem("carousel_session", JSON.stringify({
        sessionId: session_id,
        groupId: group_id,
        groupNumber: group_number,
        joinCode: trimmedCode,
        name: participantName.trim()
      }));

      toast.success(`Welcome ${participantName}! You're in Group ${group_number}`);
      navigate(`/session/${session_id}`);
    } catch (error) {
      console.error("Error joining session:", error);
      if (error.response?.status === 404) {
        toast.error("Session not found. Check your code.");
        setStep("code");
      } else if (error.response?.status === 403) {
        // Lobby not open yet
        toast.error(error.response.data.detail || "Session not ready yet. Please wait for the facilitator.", { duration: 5000 });
      } else if (error.response?.status === 409) {
        // Name already taken in this session
        toast.error(error.response.data.detail || "That name is already taken. Try adding an initial.", { duration: 7000 });
        setStep("name");
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.detail || "Cannot join this session");
      } else {
        toast.error("Failed to join session");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecover = async (e) => {
    e.preventDefault();
    
    if (!participantName.trim()) {
      toast.error("Please enter your name");
      return;
    }

    const trimmedCode = joinCode.trim().toUpperCase();
    setIsRecovering(true);
    
    try {
      // Generate new device ID for recovery
      const newDeviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const response = await axios.post(`${API}/sessions/recover-participant`, {
        join_code: trimmedCode,
        name: participantName.trim(),
        new_device_id: newDeviceId
      });

      const { session_id, group_id, group_number, name } = response.data;
      
      // Save new device ID
      localStorage.setItem("carousel_device_id", newDeviceId);
      localStorage.setItem("carousel_participant_name", name);
      localStorage.setItem("carousel_session", JSON.stringify({
        sessionId: session_id,
        groupId: group_id,
        groupNumber: group_number,
        joinCode: trimmedCode,
        name: name
      }));

      toast.success(`Welcome back ${name}! You're in Group ${group_number}`);
      navigate(`/session/${session_id}`);
    } catch (error) {
      console.error("Error recovering:", error);
      if (error.response?.status === 404) {
        toast.error("No participant found with that name. Try joining as new.");
        setStep("name");
      } else {
        toast.error("Failed to recover session");
      }
    } finally {
      setIsRecovering(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-20 right-20 w-72 h-72 bg-[#CCFF00] rounded-full opacity-10 blur-[100px]" />
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#8B5CF6] rounded-full opacity-15 blur-[120px]" />

      {/* QR Scanner Overlay */}
      <AnimatePresence>
        {showScanner && (
          <QRScanner 
            onScan={handleQRScan} 
            onClose={() => setShowScanner(false)} 
          />
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full relative z-10"
      >
        {/* Back button */}
        <button
          data-testid="back-btn"
          onClick={() => {
            if (step === "recover") setStep("name");
            else if (step === "name" && !code) setStep("code");
            else navigate("/");
          }}
          className="flex items-center gap-2 text-[#E2D9F3] hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#E91E8C] to-[#8B5CF6] mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {step === "recover" ? "Recover Access" : "Join Workshop"}
          </h1>
          <p className="text-[#E2D9F3]">
            {step === "code" && "Enter your session code"}
            {step === "name" && "Enter your name to join"}
            {step === "recover" && "Enter your name to recover your spot"}
          </p>
        </div>

        {step === "code" && (
          <form onSubmit={handleCodeSubmit} className="glass-heavy rounded-2xl p-8">
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#E2D9F3] mb-2">
                Session Code
              </label>
              <Input
                data-testid="join-code-input"
                type="text"
                placeholder="XXXXXX"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                maxLength={6}
                className="input-dark text-2xl text-center uppercase font-mono tracking-[0.3em] h-16"
                autoFocus
              />
            </div>

            <Button
              data-testid="next-btn"
              type="submit"
              disabled={joinCode.length < 4}
              className="w-full btn-action control-btn text-lg py-6"
            >
              Next
            </Button>

            <p className="text-center text-[#6B5E7C] text-sm mt-4">
              Ask your facilitator for the join code
            </p>
            
            <div className="border-t border-white/10 mt-6 pt-6">
              <Button
                data-testid="scan-qr-btn"
                onClick={() => setShowScanner(true)}
                className="w-full btn-primary-gradient control-btn"
              >
                <QrCode className="w-5 h-5 mr-2" />
                Scan QR Code
              </Button>
            </div>
          </form>
        )}

        {step === "name" && (
          <form onSubmit={handleJoin} className="glass-heavy rounded-2xl p-8">
            <div className="mb-4 text-center">
              <span className="text-sm text-[#E2D9F3]">Joining session</span>
              <p className="text-xl font-mono font-bold gradient-text">{joinCode}</p>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#E2D9F3] mb-2">
                <User className="w-4 h-4 inline mr-2" />
                Your Name
              </label>
              <Input
                data-testid="name-input"
                type="text"
                placeholder="Enter your name"
                value={participantName}
                onChange={(e) => setParticipantName(e.target.value)}
                className="input-dark text-lg h-14"
                autoFocus
              />
              <p className="text-xs text-[#6B5E7C] mt-2">
                Your name will be visible to the facilitator
              </p>
            </div>

            <Button
              data-testid="join-btn"
              type="submit"
              disabled={isLoading || !participantName.trim()}
              className="w-full btn-action control-btn text-lg py-6"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Joining...
                </>
              ) : (
                "Join Session"
              )}
            </Button>

            <button
              type="button"
              onClick={() => setStep("recover")}
              className="w-full mt-4 text-sm text-[#E2D9F3] hover:text-white transition-colors flex items-center justify-center gap-2 py-2"
            >
              <RefreshCw className="w-4 h-4" />
              Lost your spot? Recover by name
            </button>
          </form>
        )}

        {step === "recover" && (
          <form onSubmit={handleRecover} className="glass-heavy rounded-2xl p-8">
            <div className="mb-4 text-center">
              <span className="text-sm text-[#E2D9F3]">Recovering in session</span>
              <p className="text-xl font-mono font-bold gradient-text">{joinCode}</p>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#E2D9F3] mb-2">
                <User className="w-4 h-4 inline mr-2" />
                Your Name (exactly as you entered before)
              </label>
              <Input
                data-testid="recover-name-input"
                type="text"
                placeholder="Enter your name"
                value={participantName}
                onChange={(e) => setParticipantName(e.target.value)}
                className="input-dark text-lg h-14"
                autoFocus
              />
              <p className="text-xs text-[#6B5E7C] mt-2">
                Enter your name exactly as you did when joining
              </p>
            </div>

            <Button
              data-testid="recover-btn"
              type="submit"
              disabled={isRecovering || !participantName.trim()}
              className="w-full btn-primary-gradient control-btn text-lg py-6"
            >
              {isRecovering ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Recovering...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Recover My Spot
                </>
              )}
            </Button>

            <button
              type="button"
              onClick={() => setStep("name")}
              className="w-full mt-4 text-sm text-[#6B5E7C] hover:text-white transition-colors py-2"
            >
              Join as new participant instead
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default JoinSession;
